import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, FileDown, Clock } from "lucide-react";
import { api, API } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";

export default function PaymentResult() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const cart = useCart();
  const sessionId = params.get("session_id");
  const paypalFlag = params.get("paypal");
  const orderIdParam = params.get("order_id");
  const [state, setState] = useState("checking");
  const [orderId, setOrderId] = useState(orderIdParam || null);
  const [receiptToken, setReceiptToken] = useState(null);

  useEffect(() => {
    let attempts = 0;
    const isCancel = window.location.pathname.includes("cancel");
    if (isCancel) { setState("cancelled"); return; }
    if (paypalFlag) { cart.clear(); setState("paypal_pending"); return; }

    const finalize = async () => {
      try {
        if (!sessionId) { setState("failed"); return; }
        const poll = async () => {
          attempts++;
          const r = await api.get(`/payments/status/${sessionId}`);
          setOrderId(r.data.order_id);
          if (r.data.receipt_token) setReceiptToken(r.data.receipt_token);
          if (r.data.payment_status === "paid") { cart.clear(); setState("paid"); return; }
          if (attempts > 8) { setState("pending"); return; }
          setTimeout(poll, 2000);
        };
        poll();
      } catch { setState("failed"); }
    };
    finalize();
  }, []); // eslint-disable-line

  const cfg = {
    checking: { icon: <Loader2 className="w-14 h-14 animate-spin text-[#D4AF37]" />, title: "Confirming your payment…", sub: "Please wait a moment." },
    paid: { icon: <CheckCircle2 className="w-14 h-14 text-[#2E4F3B]" />, title: "Reservation confirmed!", sub: "A confirmation & receipt have been sent. We can't wait to make your event beautiful." },
    paypal_pending: { icon: <Clock className="w-14 h-14 text-[#B56D22]" />, title: "Thank you — payment received", sub: "Your reservation is being held. We'll confirm it as soon as your PayPal payment is verified, and you'll get a confirmation with your receipt." },
    pending: { icon: <Loader2 className="w-14 h-14 text-[#B56D22]" />, title: "Payment processing", sub: "This is taking longer than usual. You'll receive a confirmation shortly." },
    cancelled: { icon: <XCircle className="w-14 h-14 text-[#8B2E2E]" />, title: "Payment cancelled", sub: "Your reservation was not completed. Your cart is saved." },
    failed: { icon: <XCircle className="w-14 h-14 text-[#8B2E2E]" />, title: "Something went wrong", sub: "We couldn't confirm your payment. Please try again." },
  }[state];

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6] px-6">
      <div className="bg-white rounded-2xl border border-[#1A1514]/10 p-12 max-w-md text-center" data-testid="payment-result">
        <div className="flex justify-center mb-6">{cfg.icon}</div>
        <h1 className="font-heading text-4xl font-light" data-testid="payment-status-title">{cfg.title}</h1>
        <p className="text-[#1A1514]/60 mt-3">{cfg.sub}</p>
        {state === "paid" && orderId && receiptToken && (
          <a href={`${API}/orders/${orderId}/receipt?token=${receiptToken}`} target="_blank" rel="noreferrer">
            <Button variant="outline" className="mt-6 rounded-full border-[#1A1514]/20" data-testid="download-receipt-btn">
              <FileDown className="w-4 h-4 mr-2" /> Download Receipt (PDF)
            </Button>
          </a>
        )}
        <Button onClick={() => nav("/")} className="mt-4 w-full rounded-full bg-[#1A1514] text-[#F9F8F6] hover:bg-[#1A1514]/90 px-8" data-testid="back-home-btn">
          Back to Home
        </Button>
      </div>
    </div>
  );
}
